import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Check, Loader2, AlertCircle, ChevronDown } from 'lucide-react';

/**
 * Shared "Sync everything" pill for the Books Analytics area.
 *
 * Runs 4 syncs SEQUENTIALLY to respect Zoho's rate limits (books=60/min,
 * crm=150/min — enforced centrally by `zoho_ratelimit.py`). Sequential also
 * gives users a per-stage progress read-out so they know which source is
 * currently pulling.
 *
 * Order matters for data integrity:
 *   1. Invoices   (foundation — payments/CN reference invoice_ids)
 *   2. Payments   (needs fresh invoices to correctly attribute cash)
 *   3. Credit Notes (needs fresh invoices for apply-mapping)
 *   4. CRM leads  (independent domain — done last)
 */

const STAGES = [
  { key: 'invoices', label: 'Invoices', endpoint: '/books/sync',
    method: 'post', kind: 'sync' },
  { key: 'payments', label: 'Payments', endpoint: '/books/payments/sync',
    method: 'post', kind: 'sync' },
  { key: 'creditnotes', label: 'Credit notes', startEndpoint: '/books/creditnotes/sync',
    statusEndpoint: '/books/creditnotes/sync-status', kind: 'background' },
  { key: 'crm_leads', label: 'CRM leads', startEndpoint: '/sales-conversion/sync',
    statusEndpoint: '/sales-conversion/sync-status', kind: 'background' },
];

const DOT = {
  idle:  'bg-slate-300',
  run:   'bg-amber-500 animate-pulse',
  done:  'bg-emerald-500',
  fail:  'bg-rose-500',
};

const GlobalSyncPill = () => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  // per-stage: { status: 'idle'|'run'|'done'|'fail', detail: string, at?: iso }
  const [stages, setStages] = useState(() => Object.fromEntries(
    STAGES.map((s) => [s.key, { status: 'idle', detail: '', at: null }]),
  ));
  const [freshness, setFreshness] = useState(null); // {label,color}
  const cancelRef = useRef(false);

  const setStage = (key, patch) =>
    setStages((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // Compute overall freshness from the oldest last-sync across sources.
  const loadFreshness = useCallback(async () => {
    try {
      const [inv, cn, crm] = await Promise.allSettled([
        api.get('/books/sync/status'),
        api.get('/books/creditnotes/sync-status'),
        api.get('/sales-conversion/sync-status'),
      ]);
      const times = [
        inv.value?.data?.last_sync_at,
        cn.value?.data?.last_sync,
        crm.value?.data?.last_sync,
      ].filter(Boolean).map((t) => new Date(t).getTime());
      if (!times.length) { setFreshness({ label: 'No sync yet', color: 'bg-slate-300' }); return; }
      const oldest = Math.min(...times);
      const mins = Math.floor((Date.now() - oldest) / 60000);
      let color = 'bg-emerald-500', label;
      if (mins < 15) label = `Fresh · ${mins}m`;
      else if (mins < 60) { color = 'bg-amber-500'; label = `${mins}m ago`; }
      else if (mins < 60 * 24) { color = 'bg-rose-500'; label = `${Math.floor(mins / 60)}h ago`; }
      else { color = 'bg-rose-500'; label = `${Math.floor(mins / 60 / 24)}d ago`; }
      setFreshness({ label, color });
    } catch { setFreshness({ label: 'Sync', color: 'bg-slate-300' }); }
  }, []);

  useEffect(() => { loadFreshness(); }, [loadFreshness]);

  const pollBackground = async (stage, maxWaitMs = 5 * 60 * 1000) => {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      if (cancelRef.current) throw new Error('Cancelled');
      await new Promise((r) => setTimeout(r, 3000));
      const r = await api.get(stage.statusEndpoint);
      const s = r.data || {};
      if (s.running) {
        const done = s.done ?? s.progress ?? 0;
        const total = s.total ?? s.total_chunks ?? '?';
        setStage(stage.key, { status: 'run', detail: `${done}/${total}` });
      } else {
        return s; // finished
      }
    }
    throw new Error('Timed out');
  };

  const runAll = async () => {
    if (running) return;
    setRunning(true);
    cancelRef.current = false;
    setOpen(true);
    // Reset states
    setStages(Object.fromEntries(STAGES.map((s) => [s.key, { status: 'idle', detail: '', at: null }])));

    for (const stage of STAGES) {
      if (cancelRef.current) break;
      setStage(stage.key, { status: 'run', detail: 'Starting…' });
      try {
        if (stage.kind === 'sync') {
          const r = await api.post(stage.endpoint);
          const d = r.data || {};
          const summary =
            stage.key === 'invoices'
              ? `${d.invoices_updated ?? 0} updated · ${d.new_invoices ?? 0} new`
              : `${d.payments_updated ?? d.updated ?? 0} updated · ${d.new_payments ?? d.new ?? 0} new`;
          setStage(stage.key, { status: 'done', detail: summary, at: new Date().toISOString() });
        } else {
          // Background start-then-poll
          await api.post(stage.startEndpoint);
          const s = await pollBackground(stage);
          const summary = stage.key === 'creditnotes'
            ? `${s.cn_count ?? 0} cached`
            : `${s.lead_count ?? 0} leads`;
          setStage(stage.key, { status: 'done', detail: summary, at: new Date().toISOString() });
        }
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || 'Failed';
        setStage(stage.key, { status: 'fail', detail: msg });
        toast.error(`${stage.label} sync failed — ${msg}`);
        // Continue to the next stage even on failure so users see the full picture
      }
    }
    setRunning(false);
    loadFreshness();
    toast.success('Sync run complete');
  };

  const pillColor = running ? 'bg-amber-500' : (freshness?.color || 'bg-slate-300');

  return (
    <div className="relative" data-testid="global-sync-pill">
      <button type="button" onClick={() => setOpen((v) => !v)}
        title="Sync Books invoices, payments, credit notes & CRM leads — sequentially, rate-limit-safe"
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-[11px] text-slate-700 shadow-sm"
        data-testid="global-sync-pill-toggle">
        <span className={`w-2 h-2 rounded-full ${pillColor}`} />
        <span className="font-medium">{running ? 'Syncing…' : (freshness?.label || 'Sync')}</span>
        <RefreshCw className={`w-3 h-3 ml-0.5 ${running ? 'animate-spin' : ''}`} />
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => !running && setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-[320px] max-w-[calc(100vw-2rem)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Sync everything</p>
              <button type="button" onClick={runAll} disabled={running} data-testid="global-sync-run"
                className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {running ? 'Running' : 'Run all'}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
              Sequential — respects Zoho's rate limits. CRM leads runs last (independent domain).
            </p>
            <div className="space-y-1.5">
              {STAGES.map((s) => {
                const st = stages[s.key];
                return (
                  <div key={s.key} className="flex items-center gap-2 text-[11.5px]" data-testid={`global-sync-stage-${s.key}`}>
                    <span className={`w-2 h-2 rounded-full ${DOT[st.status]}`} />
                    <span className="font-medium text-slate-800 min-w-[86px]">{s.label}</span>
                    <span className="flex-1 text-slate-500 truncate">
                      {st.status === 'idle' && '—'}
                      {st.status === 'run' && st.detail}
                      {st.status === 'done' && <span className="text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3" /> {st.detail}</span>}
                      {st.status === 'fail' && <span className="text-rose-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {st.detail}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
            {running && (
              <button type="button" onClick={() => { cancelRef.current = true; }}
                className="mt-2 w-full text-[10px] text-slate-500 hover:text-rose-700"
                data-testid="global-sync-cancel">Stop after current stage</button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalSyncPill;
